import { AppColorVariant } from "../theme/AppColorVariant";
import { ConflictReason } from "./ConflictReason";
import { ConflictResolutionAction } from "./ConflictResolutionAction";
import { MigrationType } from "./Statistics/MigrationType";

/**
 * A conflict rule as the server reads it. The rule form edits exactly this shape, and it travels to the server as JSON
 * text (`JSON.stringify`), never as a bound object: saved rules and run trails keep the text as it was sent.
 *
 * Saved rules and audit trails keep property names, enum values and attribute/operator keys for good, so they mirror
 * the server's `ConflictRuleDefinition` exactly and are never renamed here.
 */
export interface ConflictRuleDefinition {
  schema: number;
  name: string | null;
  appliesTo: RuleScope;
  winner: RuleWinner;
  otherCopies: RuleOtherCopies;
  checks: RuleChecks;
  options: RuleOptions;
}

/** The only rule schema the server reads. */
export const CONFLICT_RULE_SCHEMA = 1;

export interface RuleScope {
  families: MigrationType[];
  reasons: ConflictReason[];
  /** Full case numbers, compared ignoring case. Only a rule on cases alone may list any. */
  caseNumbers: string[];
}

export enum WinnerKind {
  MatchingCopy = "matchingCopy",
  InSharePoint = "inSharePoint",
  None = "none",
}

export const WinnerKindLabels: Record<WinnerKind, string> = {
  [WinnerKind.MatchingCopy]: "A copy matching conditions",
  [WinnerKind.InSharePoint]: "The copy already in SharePoint",
  [WinnerKind.None]: "No winner",
};

export interface RuleWinner {
  kind: WinnerKind;
  /** Only for {@link WinnerKind.MatchingCopy}. */
  when: RuleCondition[];
}

/** The actions a rule can give the copies other than the winner. Replace from disk is the winner's alone. */
export const OTHER_COPIES_ACTIONS: ConflictResolutionAction[] = [
  ConflictResolutionAction.RenameKeepBoth,
  ConflictResolutionAction.AcceptSharePoint,
  ConflictResolutionAction.SkipAcknowledge,
];

export interface RuleOtherCopies {
  action: ConflictResolutionAction;
  /** Every flagged copy other than the winner must match all of these, or the group is skipped. */
  onlyIf: RuleCondition[];
}

export interface RuleCondition {
  /** A key of the catalog's attributes. */
  attribute: string;
  /** Only for an attribute that takes a folder name; omitted otherwise, because the server refuses it there. */
  folder?: string | null;
  /** A key of the catalog's operators. */
  operator: string;
  /** Null until someone fills it in: a built-in rule can leave a value for each firm to enter, and the server refuses
   * to preview the rule until it is. An empty string is a real value ("is" empty). */
  value: string | null;
}

export interface RuleCopyCount {
  exactly?: number | null;
  atLeast?: number | null;
}

export interface RuleChecks {
  copyCount: RuleCopyCount | null;
  sameSize: boolean;
  differentFolders: string | null;
}

export interface RuleOptions {
  allowReplacingCopiesInSharePoint: boolean;
  allowAcceptingDifferentContent: boolean;
}

/** One finding about a rule, pinned by JSON pointer (`/winner/when/0/value`), or `/` for the whole rule. */
export interface RuleDiagnostic {
  pointer: string;
  message: string;
}

export interface ConflictRuleValidation {
  /** Stop a preview, a run and a save. */
  errors: RuleDiagnostic[];
  /** Never block anything. */
  warnings: RuleDiagnostic[];
}

export interface RuleCatalogEntry {
  key: string;
  label: string;
}

export interface ConflictRuleAttribute extends RuleCatalogEntry {
  /** A condition on it names a folder, such as "Folder after". */
  takesFolder: boolean;
}

/** The vocabulary the rule form builds its menus from. */
export interface ConflictRuleCatalog {
  attributes: ConflictRuleAttribute[];
  operators: RuleCatalogEntry[];
  /** Labels of the fixed checks, keyed by their property in the rule. */
  checks: RuleCatalogEntry[];
  /** Labels of the options, keyed by their property in the rule. */
  options: RuleCatalogEntry[];
  /** What a condition value can hold in place of a group's own value, keyed by the text to insert. */
  placeholders: RuleCatalogEntry[];
  /** Why a rule leaves a group alone, keyed as the API writes the check, in the order the checks run. */
  skips: RuleCatalogEntry[];
}

/** Where a copy stood when the rule looked. Mirrors the server's `ConflictClaimantState`. */
export enum ConflictClaimantState {
  Flagged = "flagged",
  Decided = "decided",
  Queued = "queued",
  InSharePoint = "inSharePoint",
  WontUpload = "wontUpload",
}

export const ConflictClaimantStateLabels: Record<ConflictClaimantState, string> = {
  [ConflictClaimantState.Flagged]: "Flagged",
  [ConflictClaimantState.Decided]: "Decided, not uploaded",
  [ConflictClaimantState.Queued]: "Queued without a flag",
  [ConflictClaimantState.InSharePoint]: "In SharePoint",
  [ConflictClaimantState.WontUpload]: "Won't upload",
};

export const ConflictClaimantStateColors: Record<ConflictClaimantState, AppColorVariant> = {
  [ConflictClaimantState.Flagged]: "danger",
  [ConflictClaimantState.Decided]: "warning",
  [ConflictClaimantState.Queued]: "warning",
  [ConflictClaimantState.InSharePoint]: "primary",
  [ConflictClaimantState.WontUpload]: "neutral",
};

/** The part a copy plays in a rule's decision for its group. */
export enum RuleCopyRole {
  Winner = "winner",
  OtherCopy = "otherCopy",
  Untouched = "untouched",
}

export const RuleCopyRoleLabels: Record<RuleCopyRole, string> = {
  [RuleCopyRole.Winner]: "Winner",
  [RuleCopyRole.OtherCopy]: "Other copy",
  [RuleCopyRole.Untouched]: "Left as it is",
};

export enum ConflictRuleOutcome {
  Decided = "decided",
  Skipped = "skipped",
}

export interface ConflictRuleCopy {
  fileId: string;
  /** The copy's source path, or its stored name when it has none. */
  sourcePath: string;
  /** Null in a run's trail, which keeps what the run did, not where each copy stood. */
  state?: ConflictClaimantState | null;
  reason?: ConflictReason | null;
  /** Null in a run's trail and for a skipped group. */
  role?: RuleCopyRole | null;
  /** The decision the rule records on the copy; null when it records none. */
  action?: ConflictResolutionAction | null;
  /** The winner a renamed or accepting copy is held for until it lands. */
  waitsForFileId?: string | null;
}

export interface ConflictRuleGroup {
  family: MigrationType;
  parentId: string;
  /** A case number, an intake number, a folder path: how people know the parent. */
  parentLabel: string;
  /** The SharePoint path the copies compete for. */
  targetPath: string;
  outcome: ConflictRuleOutcome;
  /** For a skipped group, the first check it failed, keyed as the catalog's skips. */
  check?: string | null;
  /** For a skipped group, why, with this group's detail. */
  label?: string | null;
  copies: ConflictRuleCopy[];
}

export interface ConflictRuleActionSummary {
  action: ConflictResolutionAction;
  fileCount: number;
  /** Up to ten groups with a copy given this decision; only in a preview. */
  samples?: ConflictRuleGroup[] | null;
}

export interface ConflictRuleSkipSummary {
  check: string;
  /** The check's label without any one group's detail. */
  label: string;
  groupCount: number;
  /** Up to ten of those groups; only in a preview. */
  samples?: ConflictRuleGroup[] | null;
}

export interface ConflictRulePreview {
  ruleName: string | null;
  groupsToDecide: number;
  /** What a run is confirmed with: it refuses to start when the rule no longer decides exactly this many files. */
  filesToDecide: number;
  actions: ConflictRuleActionSummary[];
  copiesThatWait: number;
  groupsSkipped: number;
  skipped: ConflictRuleSkipSummary[];
  /** For each ticked option, how many copies it affects. */
  warnings: RuleDiagnostic[];
}

export interface ConflictRuleRunResult {
  /** The run's Conflict Rule Run audit entry. */
  auditLogId: string;
  groupsDecided: number;
  filesDecided: number;
  actions: ConflictRuleActionSummary[];
  copiesThatWait: number;
  groupsSkipped: number;
  skipped: ConflictRuleSkipSummary[];
}

/** How far a preview or a run has got. Every figure counts work already done. */
export interface ConflictRuleProgress {
  /** Parents a preview has evaluated, or parents whose chunk a run has committed. */
  parentsDone: number;
  parentCount: number;
  groupsDecided: number;
  filesDecided: number;
  groupsSkipped: number;
}

export interface ConflictRuleRunGroups {
  ruleName: string | null;
  /** The rule's JSON text exactly as it ran. */
  rule: string;
  decidedGroupCount: number;
  skipped: ConflictRuleSkipSummary[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  groups: ConflictRuleGroup[];
}

/** A rule saved to the shared library, the same in every organization. */
export interface SavedConflictRule {
  id: string;
  name: string;
  description: string | null;
  /** The rule's JSON text, exactly as it was saved. */
  definition: string;
  /** The version an update of this rule must name. */
  version: number;
  createdBy: string;
  createdOn: string;
  modifiedBy: string | null;
  modifiedOn: string | null;
}

/** A built-in rule. It cannot be changed or deleted, only copied into the form. */
export interface ConflictRulePreset {
  key: string;
  name: string;
  definition: string;
}

export interface ConflictRuleLibrary {
  presets: ConflictRulePreset[];
  rules: SavedConflictRule[];
}

/** An empty rule: cases, every conflict type to be chosen, a winner picked by conditions, renamed other copies. */
export const emptyConflictRule = (): ConflictRuleDefinition => ({
  schema: CONFLICT_RULE_SCHEMA,
  name: "",
  appliesTo: { families: [MigrationType.CasesMigration], reasons: [], caseNumbers: [] },
  winner: { kind: WinnerKind.MatchingCopy, when: [] },
  otherCopies: { action: ConflictResolutionAction.RenameKeepBoth, onlyIf: [] },
  checks: { copyCount: null, sameSize: false, differentFolders: null },
  options: { allowReplacingCopiesInSharePoint: false, allowAcceptingDifferentContent: false },
});

// Stands in for the case list while the rest is indented, then is swapped for the list on one line.
const CASE_NUMBERS_SLOT = "\u0000caseNumbers\u0000";

/**
 * The rule as the text the server reads. A winner that is not picked by conditions takes none, so conditions kept in
 * the form while another kind is chosen are left out rather than refused. The case list goes on one line: indented one
 * entry per line, about 800 case numbers would reach the server's limit on the whole text before its 1,000-case limit.
 */
export const serializeConflictRule = (rule: ConflictRuleDefinition): string =>
  JSON.stringify(
    {
      ...rule,
      appliesTo: { ...rule.appliesTo, caseNumbers: CASE_NUMBERS_SLOT },
      winner: {
        kind: rule.winner.kind,
        when: rule.winner.kind === WinnerKind.MatchingCopy ? rule.winner.when : [],
      },
    },
    null,
    2
  ).replace(JSON.stringify(CASE_NUMBERS_SLOT), () => JSON.stringify(rule.appliesTo.caseNumbers));

/**
 * Router state that opens the rule page with a rule already in the form, such as the rule a past run recorded. The
 * page reads it once, when it opens.
 */
export interface LoadConflictRuleState {
  /** The rule's JSON text. */
  conflictRule: string;
}

/** The rule page of one organization. */
export const conflictRulePagePath = (organizationId: string): string =>
  `/organization/${organizationId}/resolve-conflicts-by-rule`;

/** Whether the rule applies to cases alone, which a case list and the case-number placeholder need. */
export const isCasesOnly = (rule: ConflictRuleDefinition): boolean =>
  rule.appliesTo.families.length > 0 &&
  rule.appliesTo.families.every((family) => family === MigrationType.CasesMigration);
